import CloseIcon from "@mui/icons-material/Close";
import { Box, CircularProgress, FormControl, IconButton } from "@mui/material";
import pluralize from "pluralize";
import React, { useMemo } from "react";

import { useImageQueriesAnswersByType } from "api/services/useImageQueriesAnswersByType";
import { DateTimeRangePicker } from "components/common/DateTimeRangePicker";
import { labelInternalToUI, labelMappings, OTHER_LABEL } from "config/LabelMapping";
import { useUser } from "hooks/user/useUser";
import { TextFieldInput } from "new-components/textFieldInput/TextFieldInput";
import { OptionType } from "new-components/textFieldInput/types";
import { DetectorModes } from "types/createDetector";
import { Detector } from "types/detectors";
import { ANSWER_VALUES } from "types/imageQueries";

import { FilterParams } from "./FiltersData";
import { FilterChanges } from "./GridFilters";

const columnsToFetch = [
  "ml_answer_str",
  "system_answer_str",
  "system_prediction_str",
  "current_best_answer_str",
  "current_best_prediction_str",
];

const defaultAnswerOption = [
  {
    name: "None",
    value: "None",
  },
];

export interface FilterComponentProps {
  detector?: Detector;
  isEdit: boolean;
  filterId: string;
  columnField: string;
  filterValue: string | string[];
  filterOperator?: string;
  handleChangeFilter: (columnField: string, value: Record<string, string | string[]>) => void;
  handleRemoveFilter: (columnField: string) => void;
  handleChangeColumn: (columnField: string, id: string) => void;
  filterChanges: FilterChanges[];
  filterableElements: FilterParams[];
}

export function FilterComponent(props: FilterComponentProps) {
  const {
    detector,
    isEdit,
    filterId,
    columnField,
    filterValue,
    filterOperator,
    handleChangeFilter,
    handleRemoveFilter,
    handleChangeColumn,
    filterChanges,
    filterableElements,
  } = props;

  const user = useUser();

  const shouldFetchAnswers =
    (detector?.mode === DetectorModes.COUNT || detector?.mode === DetectorModes.MULTICHOICE) &&
    columnsToFetch.includes(columnField);

  const { imageQueriesAnswersByType, isLoadingImageQueriesAnswersByType } = useImageQueriesAnswersByType({
    getOptions: {
      params: {
        detectorId: detector?.id,
        answerType: columnField,
      },
      options: {
        enabled: shouldFetchAnswers && !!detector?.id,
      },
    },
  });

  const data: FilterParams = useMemo(() => {
    const findData = filterableElements.find((p) => p.columnField === columnField);
    if (findData) {
      // MULTICHOICE mode
      if (
        detector?.mode === DetectorModes.MULTICHOICE &&
        imageQueriesAnswersByType?.answers &&
        columnsToFetch.includes(columnField)
      ) {
        const mapAnswers =
          imageQueriesAnswersByType.answers.length > 0
            ? imageQueriesAnswersByType.answers
                .sort((a, b) => {
                  const isAUnclearOrOther =
                    // eslint-disable-next-line no-underscore-dangle
                    a === ANSWER_VALUES.UNCLEAR || a === ANSWER_VALUES.__UNSURE || a === OTHER_LABEL;
                  const isBUnclearOrOther =
                    // eslint-disable-next-line no-underscore-dangle
                    b === ANSWER_VALUES.UNCLEAR || b === ANSWER_VALUES.__UNSURE || b === OTHER_LABEL;

                  if (isAUnclearOrOther && !isBUnclearOrOther) return 1;
                  if (!isAUnclearOrOther && isBUnclearOrOther) return -1;
                  return 0;
                })
                .map((answerValue) => ({
                  value: answerValue.toUpperCase() === ANSWER_VALUES.UNCLEAR ? "unclear" : answerValue,
                  name:
                    // eslint-disable-next-line no-underscore-dangle
                    answerValue === ANSWER_VALUES.UNCLEAR || answerValue === ANSWER_VALUES.__UNSURE
                      ? "Unclear"
                      : answerValue,
                }))
            : defaultAnswerOption;
        return {
          ...findData,
          valueOptions: mapAnswers,
        };
      }
      // COUNT mode
      if (
        detector?.mode === DetectorModes.COUNT &&
        imageQueriesAnswersByType?.answers &&
        columnsToFetch.includes(columnField)
      ) {
        const mapAnswers =
          imageQueriesAnswersByType.answers.length > 0
            ? imageQueriesAnswersByType.answers.map((answerValue) => {
                const mappedLabel = labelInternalToUI(labelMappings, answerValue);
                let pluralizedSuffix = "";
                if (mappedLabel.toUpperCase() !== ANSWER_VALUES.UNCLEAR) {
                  if (mappedLabel === ">") {
                    pluralizedSuffix = pluralize("Object", detector.mode_configuration?.max_count || 0);
                  } else {
                    pluralizedSuffix = pluralize("Object", parseInt(answerValue, 10));
                  }
                }

                return {
                  value: mappedLabel.toUpperCase() === ANSWER_VALUES.UNCLEAR ? "unclear" : answerValue,
                  name: mappedLabel.toUpperCase() === ANSWER_VALUES.UNCLEAR ? "Unclear" : mappedLabel,
                  suffix: pluralizedSuffix,
                };
              })
            : defaultAnswerOption;
        return {
          ...findData,
          valueOptions: mapAnswers,
        };
      }

      return findData;
    }
    return filterableElements[0];
  }, [
    filterableElements,
    columnField,
    detector?.mode,
    imageQueriesAnswersByType,
    detector?.mode_configuration?.max_count,
  ]);

  const filterChangesSet: Set<string> = useMemo(
    () => new Set(filterChanges.map((filter) => filter.columnField)),
    [filterChanges]
  ); // Convert filterChanges to a Set for faster lookups

  const isColumnDisabled = (columnFieldValue: string) => {
    if (isEdit) return false;

    return filterChangesSet.has(columnFieldValue);
  };

  const handleChangeOperator = (columnField: string, value: string) => {
    if (data.type === "categorical" && data.valueOptions) {
      handleChangeFilter(columnField, { operatorValue: value, value: data.valueOptions[0].value });
      return;
    }

    handleChangeFilter(columnField, { operatorValue: value });
  };

  const changeColumn = (columnField: string, filterId: string) => {
    handleChangeColumn(columnField, filterId);
  };

  const valueOptions: OptionType<string>[] = (data.valueOptions ?? []).map((option) => {
    const suffix = data.valueOptions?.find((v) => v.value === option.value)?.suffix ?? "";
    return {
      label: `${option.name} ${suffix}`,
      value: option.value,
    };
  });
  const operationOptions: OptionType<string>[] = (data.operatorOptions ?? []).map((option) => ({
    label: option.name,
    value: option.value,
  }));
  const columnOptions: OptionType<string>[] = (filterableElements ?? []).map((option) => ({
    label: option.headerName,
    value: option.columnField,
    disabled: isColumnDisabled(option.columnField),
  }));

  return (
    <Box className="flex justify-start gap-2">
      {!isEdit && (
        <IconButton onClick={() => handleRemoveFilter(data.columnField)}>
          <CloseIcon />
        </IconButton>
      )}

      <FormControl className="w-[360px]">
        <TextFieldInput
          id="simple-select-columns"
          variant="select"
          selectedOption={columnOptions.find((option) => option.value === columnField)}
          options={columnOptions}
          onChange={(e) => changeColumn(e.target.value, filterId)}
          disabled={isEdit}
        />
      </FormControl>

      {data.operatorOptions && (
        <FormControl className="w-[150px]">
          <TextFieldInput
            id="simple-select-operator"
            variant="select"
            selectedOption={operationOptions.find((option) => option.value === filterOperator)}
            options={operationOptions}
            onChange={(e: any) => handleChangeOperator(columnField, e.target.value)}
          />
        </FormControl>
      )}

      {isLoadingImageQueriesAnswersByType ? (
        <CircularProgress />
      ) : (
        <Box>
          {(data.type === "categorical" || data.type === "multiselect") && (
            <FormControl className="w-[370px]">
              <TextFieldInput
                id="simple-select-operator"
                variant="select"
                multiple={data.type === "multiselect" && user.enable_data_page_multiselect_filter}
                selectedOption={
                  data.type === "multiselect" && user.enable_data_page_multiselect_filter
                    ? valueOptions.filter((option) => filterValue.includes(option.value))
                    : valueOptions.find((option) => filterValue.includes(option.value))
                }
                options={valueOptions}
                onChange={(e) => handleChangeFilter(columnField, { value: e.target.value })}
                onSelectionChange={(selection) => {
                  handleChangeFilter(columnField, {
                    value: selection.map((option) => option.value),
                  });
                }}
              />
            </FormControl>
          )}
        </Box>
      )}

      {data.type === "textfield" && (
        <FormControl className="w-[150px]">
          <TextFieldInput
            id="simple-textfield-value"
            variant="standard"
            valueType={data.typeText === "number" ? "numeric" : "text"}
            placeholder={data.typeText === "number" ? "Enter Number" : "Enter Text"}
            value={typeof filterValue === "string" ? filterValue : filterValue.join(", ")}
            onChange={(e: any) => handleChangeFilter(columnField, { value: e.target.value })}
          />
        </FormControl>
      )}

      {data.type === "date" && (
        <DateTimeRangePicker
          value={Array.isArray(filterValue) ? filterValue[0] : filterValue}
          handleChangeDate={handleChangeFilter}
          valueOptions={data.valueOptions}
        />
      )}
    </Box>
  );
}
